import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { css } from '@understory-io/leaf/css';
import { Stack } from '@understory-io/leaf/stack';
import type Hls from 'hls.js';
import { Image } from '~/ui/image';
import { Lightbox } from '~/ui/lightbox';
import { PlayIcon } from '~/ui/icons/play';
import type { GalleryMedia } from './gallery';

type MediaLightboxProps = {
  media: GalleryMedia[];
  /**
   * Index into `media` of the item the user clicked, or `null` to keep the
   * lightbox closed. When non-null, the lightbox opens and scrolls so the
   * matching item is at the top of the viewport.
   */
  openAtIndex: number | null;
  onClose: () => void;
};

const SAFE_MEDIA_SCHEMES = /^https?:/i;

// Tell hls.js to assume a fat pipe so its ABR picks the top variant from
// the first segment, instead of starting low and ramping up. Mirrors the
// legacy storefront's video.js setting (apps/web video-js.tsx). 100 Mbps in
// bits/sec.
const HIGH_BANDWIDTH_ESTIMATE = 100 * 1024 * 1024;

function isSafeMediaUrl(url: string): boolean {
  return SAFE_MEDIA_SCHEMES.test(url);
}

function isHlsUrl(url: string): boolean {
  // Detect HLS by pathname extension only — see GalleryVideo for the same
  // logic, this is intentionally duplicated since the playback configuration
  // (controls, no autoplay) differs from the gallery's ambient loop.
  try {
    return new URL(url).pathname.toLowerCase().endsWith('.m3u8');
  } catch {
    return false;
  }
}

type Renderable =
  { type: 'image'; url: string } | { type: 'video'; url: string };

export function MediaLightbox({
  media,
  openAtIndex,
  onClose,
}: MediaLightboxProps) {
  const intl = useIntl();
  const renderable = media.filter(
    (item): item is Renderable =>
      (item.type === 'image' || item.type === 'video') &&
      isSafeMediaUrl(item.url)
  );
  const itemRefs = useRef<Array<HTMLLIElement | null>>([]);
  const isOpen = openAtIndex !== null;

  // Map the clicked media index onto the filtered list. Falls back to the
  // first item if the clicked tile was filtered out (unsafe URL, unknown
  // type, or closed state).
  const clickedItem = openAtIndex !== null ? media[openAtIndex] : undefined;
  const targetIndex = clickedItem
    ? Math.max(
        0,
        renderable.findIndex(
          (item) =>
            item.type === clickedItem.type && item.url === clickedItem.url
        )
      )
    : 0;

  // Scroll to the requested item whenever the lightbox transitions open.
  // `auto` (instant) is used because the dialog itself just animated in; a
  // smooth scroll layered on top would feel sluggish.
  useEffect(() => {
    if (!isOpen) return;
    const target = itemRefs.current[targetIndex];
    target?.scrollIntoView({ behavior: 'auto', block: 'start' });
  }, [isOpen, targetIndex]);

  if (renderable.length === 0) return null;

  const ariaLabel = intl.formatMessage({
    defaultMessage: 'Gallery',
    description: 'Accessible name for the experience gallery shown fullscreen',
  });

  return (
    <Lightbox isOpen={isOpen} onClose={onClose} ariaLabel={ariaLabel}>
      <Stack as="ol" gap="large" className={listStyles}>
        {renderable.map((item, i) => (
          <li
            key={`${item.type}:${item.url}`}
            ref={(node) => {
              itemRefs.current[i] = node;
            }}
            className={itemStyles}
          >
            {item.type === 'image' ? (
              <Image
                src={item.url}
                alt=""
                layout="fullWidth"
                // `itemStyles` caps the photo and centres it, so the viewport
                // width is only the right answer below that cap.
                sizes="(min-width: 1000px) 1000px, 100vw"
                priority={i === targetIndex}
              />
            ) : (
              <LightboxVideo url={item.url} />
            )}
          </li>
        ))}
      </Stack>
    </Lightbox>
  );
}

function LightboxVideo({ url }: { url: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  // Show a centered play button until the user starts playback. After the
  // first play, the native `<video controls>` UI takes over for everything
  // else (pause, seek, volume, fullscreen).
  const [hasStarted, setHasStarted] = useState(false);
  const intl = useIntl();
  const playLabel = intl.formatMessage({
    defaultMessage: 'Play video',
    description:
      'Action that starts playback of a gallery video in the lightbox',
  });

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isHlsUrl(url)) return;

    // Safari/iOS plays HLS natively. No autoplay kick-off here — the user
    // controls when playback starts via the overlay or native controls.
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = url;
      return () => {
        video.removeAttribute('src');
        video.load();
      };
    }

    // Other browsers: lazy-load hls.js and attach.
    let hls: Hls | null = null;
    let cancelled = false;
    void import('hls.js').then(({ default: HlsCtor }) => {
      if (cancelled || !HlsCtor.isSupported()) return;
      hls = new HlsCtor({ abrEwmaDefaultEstimate: HIGH_BANDWIDTH_ESTIMATE });
      hls.loadSource(url);
      hls.attachMedia(video);
    });

    return () => {
      cancelled = true;
      hls?.destroy();
    };
  }, [url]);

  const handlePlay = () => {
    setHasStarted(true);
    void videoRef.current?.play().catch(() => {});
  };

  return (
    <div className={videoWrapperStyles}>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption -- host-uploaded promo media has no caption track to attach */}
      <video
        ref={videoRef}
        src={isHlsUrl(url) ? undefined : url}
        controls
        playsInline
        preload="metadata"
        className={videoStyles}
        onPlay={() => setHasStarted(true)}
      />
      {!hasStarted && (
        <button
          type="button"
          onClick={handlePlay}
          aria-label={playLabel}
          className={playButtonStyles}
        >
          <span className={playIconStyles} aria-hidden="true">
            <PlayIcon />
          </span>
        </button>
      )}
    </div>
  );
}

// Vertical scroll container — fills the lightbox content area and stacks the
// items top to bottom. Reset list semantics so the items render flush.
const listStyles = css({
  position: 'absolute',
  inset: 'none',
  overflowY: 'auto',
  listStyle: 'none',
  margin: 'none',
  padding: 'none',
  background: 'surface.base',
});

// Each item is centered horizontally and capped at 1000px wide so portrait
// or oversize media doesn't blow up the layout on ultra-wide displays. The
// intrinsic aspect ratio governs the rendered height, so the user scrolls to
// move between items.
const itemStyles = css({
  width: 'full',
  maxWidth: '[1000px]',
  marginX: 'auto',
  display: 'block',
  '& img': {
    width: 'full',
    height: 'auto',
    display: 'block',
  },
});

// Video wrapper — relative positioning lets the play overlay anchor to the
// video. Black background so portrait videos in a 16:9 container letterbox
// cleanly instead of showing the page background through the gaps.
const videoWrapperStyles = css({
  position: 'relative',
  width: 'full',
  background: '[black]',
});

const videoStyles = css({
  width: 'full',
  height: 'auto',
  display: 'block',
});

// Centered play button overlay — shown until the user starts playback. The
// click target fills the video area so users can hit play from anywhere on
// the frame, but the visible chrome is just the round badge in the middle.
const playButtonStyles = css({
  position: 'absolute',
  inset: '[0]',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '[transparent]',
  border: 'none',
  padding: 'none',
  cursor: 'interactive',
});

const playIconStyles = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '[72px]',
  height: '[72px]',
  borderRadius: 'full',
  background: 'surface.base',
  color: 'content.primary',
  boxShadow: '[0 4px 15px 0 rgba(14, 15, 12, 0.2)]',
  '& svg': {
    width: '[36px]',
    height: '[36px]',
    // Optical centering: the play triangle is right-heavy, so nudge it left
    // a hair so it visually sits in the middle of the round badge.
    marginLeft: '[4px]',
  },
});
